import { useEffect, useState } from 'preact/hooks';
import { Fragment } from 'preact/jsx-runtime';
import { useSelect } from 'downshift';

import Shipment from 'interfaces/PmParcel';
import {
  SelectContainer,
  SelectWrapper,
  SelectLabel,
  SelectToggleButton,
  SelectMenu,
  SelectItem,
  SelectNoResults,
  SelectHelperText,
  SelectValue,
  Container,
  StyledChevronIcon,
} from './Select.styled';
import { DisplayItem } from '../DisplayItem';

interface SelectV2Props
  extends Omit<React.HTMLProps<HTMLButtonElement>, 'onSelect' | 'value'> {
  options: Shipment[];
  value?: string;
  hasError?: boolean;
  helpText?: string;
  dropdownTitle?: string;
  customDropdownTitle?: React.ReactNode;
  onSelect?: (value: string) => void;
}

export const Select = ({
  options = [],
  value = '',
  hasError = false,
  helpText,
  dropdownTitle,
  customDropdownTitle,
  placeholder,
  onSelect,
  'aria-describedby': ariaDescribedBy,
  ...props
}: SelectV2Props) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  // Item to string conversion for display
  const itemToString = (item: Shipment | null) => {
    return item ? item?.poId?.toString?.() : '';
  };

  // Find initial selected item
  const getInitialSelectedItem = () => {
    return options.find((option) => option.poId?.toString() === value) || null;
  };

  const {
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    isOpen,
    items: options,
    selectedItem: selectedValue,
    itemToString,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        setSelectedValue(selectedItem);
        onSelect?.(selectedItem.poId.toString());
      }
    },
    onIsOpenChange: ({ isOpen }) => {
      setIsOpen(isOpen);
    },
  });

  // Update selected item when value prop changes
  useEffect(() => {
    const newSelectedItem = getInitialSelectedItem();
    if (newSelectedItem) {
      setSelectedValue(newSelectedItem);
    }
  }, [value, options]);

  return (
    <Container>
      <SelectContainer
        data-testid="select-container"
        hasError={hasError}
        isCustom={!!customDropdownTitle}
        aria-label={customDropdownTitle || dropdownTitle || placeholder}
        {...getToggleButtonProps()}
        {...(props || {})}
      >
        <SelectWrapper spaceBetween={!!customDropdownTitle}>
          <SelectLabel
            {...getLabelProps({
              id: null,
              for: '',
            })}
          >
            <SelectValue>
              {customDropdownTitle || dropdownTitle || placeholder}
            </SelectValue>
          </SelectLabel>

          <SelectToggleButton isMenuOpen={isOpen}>
            <StyledChevronIcon />
          </SelectToggleButton>
        </SelectWrapper>
      </SelectContainer>
      <SelectMenu data-testid="select-menu" isOpen={isOpen} {...getMenuProps()}>
        {isOpen && (
          <Fragment>
            {options.length > 0 ? (
              options.map((item, index) => (
                <SelectItem
                  data-testid="select-item"
                  {...getItemProps({
                    item,
                    index,
                  })}
                  key={item?.poId}
                  isHighlighted={highlightedIndex === index}
                >
                  <DisplayItem baseShipment={item} isDropdownItem />
                </SelectItem>
              ))
            ) : (
              <SelectNoResults data-testid="select-no-results">
                No Options
              </SelectNoResults>
            )}
          </Fragment>
        )}
      </SelectMenu>
      {helpText && (
        <SelectHelperText
          data-testid="select-helper-text"
          isError={hasError}
          size="sm"
          role={hasError ? 'alert' : undefined}
          aria-live={hasError ? 'polite' : undefined}
        >
          {helpText}
        </SelectHelperText>
      )}
    </Container>
  );
};
